import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { agencyAPI } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import PushNotificationsManager from '../../components/integrations/PushNotificationsManager';
import GoogleBusinessConnect from '../../components/integrations/GoogleBusinessConnect';
import WidgetEmbedCode from '../../components/integrations/WidgetEmbedCode';
import QRCodeSection from '../../components/qr/QRCodeSection';
import {
    Building2,
    Save,
    Loader2,
    ExternalLink,
    Upload,
    Image as ImageIcon,
    Settings,
    Bell,
    User,
    MapPin
} from 'lucide-react';

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
};

// Helper to generate slug
function generateSlug(name) {
    let slug = name.toLowerCase().trim();
    slug = slug.replace(/[àáâãäå]/g, 'a');
    slug = slug.replace(/[èéêë]/g, 'e');
    slug = slug.replace(/[ìíîï]/g, 'i');
    slug = slug.replace(/[òóôõö]/g, 'o');
    slug = slug.replace(/[ùúûü]/g, 'u');
    slug = slug.replace(/[ç]/g, 'c');
    slug = slug.replace(/[^a-z0-9]+/g, '-');
    slug = slug.replace(/^-|-$/g, '');
    return slug;
}

function SettingsPage() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    const { theme, isDark } = useTheme();
    const [agency, setAgency] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        siret: '',
        address: '',
        city: '',
        postal_code: '',
        email: '',
        phone: '',
        logo_url: '',
        mandate_prefix: '',
        primary_color: '#681793',
        secondary_color: '#FF914D'
    });

    const loadAgency = useCallback(async () => {
        try {
            const response = await agencyAPI.getMyAgency();
            setAgency(response.data);
            setFormData({
                name: response.data.name || '',
                siret: response.data.siret || '',
                address: response.data.address || '',
                city: response.data.city || '',
                postal_code: response.data.postal_code || '',
                email: response.data.email || '',
                phone: response.data.phone || '',
                logo_url: response.data.logo_url || '',
                mandate_prefix: response.data.mandate_prefix || '',
                primary_color: response.data.primary_color || '#681793',
                secondary_color: response.data.secondary_color || '#FF914D'
            });
        } catch (err) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAgency();
    }, [loadAgency]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await agencyAPI.updateAgency(formData);
            toast.success('Paramètres enregistrés');
            loadAgency();
        } catch (err) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-12 w-64 bg-muted" />
                <Skeleton className="h-96 w-full bg-muted" />
            </div>
        );
    }

    return (
        <motion.div 
            className="space-y-8" 
            data-testid="settings-page"
            initial="hidden"
            animate="visible"
            variants={stagger}
        >
            {/* Header */}
            <motion.div variants={fadeIn}>
                <div className="flex items-center gap-3 mb-2">
                    <Settings className="w-7 h-7 text-[#681793]" />
                    <h1 className="text-3xl font-black text-foreground">Paramètres</h1>
                </div>
                <p className="text-muted-foreground">
                    Gérez les informations de votre agence
                </p>
            </motion.div>

            {/* Agency settings */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-6 border-b border-border">
                        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-[#FF914D]" />
                            Informations de l'agence
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            {isAdmin 
                                ? "Ces informations apparaîtront sur vos mandats et documents"
                                : "Seul le directeur d'agence peut modifier ces informations"
                            }
                        </p>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <Label htmlFor="name" className="text-muted-foreground">Nom de l'agence</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => isAdmin && setFormData({ ...formData, name: e.target.value })}
                                    readOnly={!isAdmin}
                                    placeholder="Immo Excellence"
                                    className={`bg-card border-border text-foreground placeholder:text-muted-foreground ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    data-testid="settings-name-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="siret" className="text-muted-foreground">SIRET</Label>
                                <Input
                                    id="siret"
                                    value={formData.siret}
                                    onChange={(e) => isAdmin && setFormData({ ...formData, siret: e.target.value })}
                                    readOnly={!isAdmin}
                                    placeholder="123 456 789 00012"
                                    className={`bg-card border-border text-foreground placeholder:text-muted-foreground ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                />
                            </div>
                            <div>
                                <Label htmlFor="phone" className="text-muted-foreground">Téléphone</Label>
                                <Input
                                    id="phone"
                                    value={formData.phone}
                                    onChange={(e) => isAdmin && setFormData({ ...formData, phone: e.target.value })}
                                    readOnly={!isAdmin}
                                    placeholder="01 23 45 67 89"
                                    className={`bg-card border-border text-foreground placeholder:text-muted-foreground ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                />
                            </div>
                        </div>

                        <div className="border-t border-border pt-6">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <Label htmlFor="address" className="text-muted-foreground">Adresse</Label>
                                    <Input
                                        id="address"
                                        value={formData.address}
                                        onChange={(e) => isAdmin && setFormData({ ...formData, address: e.target.value })}
                                        readOnly={!isAdmin}
                                        placeholder="15 rue de la Paix"
                                        className={`bg-card border-border text-foreground placeholder:text-muted-foreground ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="postal_code" className="text-muted-foreground">Code postal</Label>
                                    <Input
                                        id="postal_code"
                                        value={formData.postal_code}
                                        onChange={(e) => isAdmin && setFormData({ ...formData, postal_code: e.target.value })}
                                        readOnly={!isAdmin}
                                        placeholder="75002"
                                        className={`bg-card border-border text-foreground placeholder:text-muted-foreground ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="city" className="text-muted-foreground">Ville</Label>
                                    <Input
                                        id="city"
                                        value={formData.city}
                                        onChange={(e) => isAdmin && setFormData({ ...formData, city: e.target.value })}
                                        readOnly={!isAdmin}
                                        placeholder="Paris"
                                        className={`bg-card border-border text-foreground placeholder:text-muted-foreground ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-border pt-6">
                            <Label htmlFor="email" className="text-muted-foreground">Email de contact</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => isAdmin && setFormData({ ...formData, email: e.target.value })}
                                readOnly={!isAdmin}
                                placeholder="contact@agence.fr"
                                className={`bg-card border-border text-foreground placeholder:text-muted-foreground ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                            />
                        </div>

                        {/* Mandate Prefix */}
                        <div className="border-t border-border pt-6">
                            <Label htmlFor="mandate_prefix" className="text-muted-foreground">
                                Préfixe des numéros de mandat
                            </Label>
                            <p className="text-xs text-muted-foreground mb-2">
                                Exemples : GIP pour "G Immobilier de Prestige" → GIP-1, GIP-2...
                            </p>
                            <div className="flex items-center gap-3">
                                <Input
                                    id="mandate_prefix"
                                    value={formData.mandate_prefix}
                                    onChange={(e) => isAdmin && setFormData({ ...formData, mandate_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                                    readOnly={!isAdmin}
                                    placeholder="MND"
                                    maxLength={10}
                                    className={`bg-card border-border text-white placeholder:text-white/30 w-32 uppercase ${!isAdmin ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    data-testid="mandate-prefix-input"
                                />
                                <span className="text-muted-foreground">-</span>
                                <span className="text-muted-foreground font-mono">123</span>
                                <span className="text-xs text-muted-foreground ml-2">(aperçu)</span>
                            </div>
                        </div>

                        {isAdmin && (
                        <div className="flex justify-end pt-4">
                            <Button 
                                onClick={handleSave} 
                                disabled={saving}
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                                data-testid="save-settings-button"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    <>
                                        <Save className="w-4 h-4 mr-2" />
                                        Enregistrer
                                    </>
                                )}
                            </Button>
                        </div>
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Google Business Profile - Admin only */}
            {user?.role === 'admin' && (
                <motion.div variants={fadeIn}>
                    <GoogleBusinessConnect />
                </motion.div>
            )}

            {/* Widget Badge Homly */}
            {agency?.id && (
                <motion.div variants={fadeIn}>
                    <WidgetEmbedCode agencyId={agency.id} />
                </motion.div>
            )}

            {/* Public Profile Card */}
            <motion.div variants={fadeIn}>
                <QRCodeSection kind="agency" />
            </motion.div>

            {/* Public Profile Card */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-6 border-b border-border">
                        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                            <ExternalLink className="w-5 h-5 text-[#681793]" />
                            Profil public
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Votre page publique visible par vos clients potentiels
                        </p>
                    </div>
                    <div className="p-6 space-y-6">
                        {/* Logo upload */}
                        <div>
                            <Label className="text-muted-foreground">Logo de l'agence</Label>
                            <div className="mt-3 flex items-center gap-6">
                                <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-white/20 flex items-center justify-center bg-card overflow-hidden">
                                    {formData.logo_url ? (
                                        <img 
                                            src={formData.logo_url} 
                                            alt="Logo" 
                                            className="w-full h-full object-contain p-2"
                                        />
                                    ) : (
                                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden"
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;
                                            
                                            const reader = new FileReader();
                                            reader.onload = (event) => {
                                                setFormData({ ...formData, logo_url: event.target.result });
                                                toast.success('Logo chargé. Cliquez sur Enregistrer pour sauvegarder.');
                                            };
                                            reader.readAsDataURL(file);
                                        }}
                                    />
                                    <Button 
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-border text-muted-foreground hover:text-foreground hover:bg-card"
                                    >
                                        <Upload className="w-4 h-4 mr-2" />
                                        Charger un logo
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        PNG, JPG. Max 2MB
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Couleurs agence */}
                        <div>
                            <Label className="text-muted-foreground">Couleurs de votre agence</Label>
                            <p className="text-xs text-muted-foreground mb-3">Visibles sur votre profil public et celui de vos agents</p>
                            <div className="flex items-center gap-6 flex-wrap">
                                <div>
                                    <p className="text-xs text-white/50 mb-1">Couleur principale</p>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={formData.primary_color || '#681793'} onChange={e => setFormData({...formData, primary_color: e.target.value})} className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent" />
                                        <span className="text-xs text-white/40 font-mono">{formData.primary_color || '#681793'}</span>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs text-white/50 mb-1">Couleur secondaire</p>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={formData.secondary_color || '#FF914D'} onChange={e => setFormData({...formData, secondary_color: e.target.value})} className="w-10 h-10 rounded-lg cursor-pointer border-0 bg-transparent" />
                                        <span className="text-xs text-white/40 font-mono">{formData.secondary_color || '#FF914D'}</span>
                                    </div>
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                    <p className="text-xs text-white/50 mb-1">Aperçu</p>
                                    <div className="h-10 rounded-lg" style={{ background: `linear-gradient(90deg, ${formData.primary_color || '#681793'}, ${formData.secondary_color || '#FF914D'})` }} />
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-border pt-6">
                            {/* Public profile link */}
                            <div className="p-5 rounded-xl bg-gradient-to-r from-[#681793]/10 to-[#FF914D]/10 border border-[#681793]/20">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <p className="font-medium text-foreground">Lien de votre profil public</p>
                                        <p className="text-sm text-muted-foreground mt-1 break-all">
                                            {window.location.origin}/agence/{generateSlug(formData.name || 'mon-agence')}
                                        </p>
                                    </div>
                                    <Link 
                                        to={`/agence/${generateSlug(formData.name || 'mon-agence')}`}
                                        target="_blank"
                                    >
                                        <Button 
                                            variant="outline"
                                            className="border-border text-muted-foreground hover:text-foreground hover:bg-card"
                                        >
                                            <ExternalLink className="w-4 h-4 mr-2" />
                                            Voir le profil
                                        </Button>
                                    </Link>
                                </div>
                            </div>

                            <p className="text-sm text-muted-foreground mt-4">
                                Partagez ce lien avec vos clients pour qu'ils puissent voir votre page publique 
                                avec vos statistiques, avis et équipe.
                            </p>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Push Notifications */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-6 border-b border-border">
                        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                            <Bell className="w-5 h-5 text-[#FF914D]" />
                            Notifications Push
                        </h2>
                        <p className="text-sm mt-1 text-muted-foreground">
                            Recevez des alertes en temps réel sur votre navigateur
                        </p>
                    </div>
                    <div className="p-6">
                        {user?.id && (
                            <PushNotificationsManager userId={user.id} />
                        )}
                    </div>
                </div>
            </motion.div>

            {/* Agent Profile Section */}
            {(user?.role === 'agent' || user?.role === 'admin') && (
                <AgentProfileSection userId={user?.id} />
            )}
        </motion.div>
    );
}

// ======== Agent Profile Section ========
function AgentProfileSection({ userId }) {
    const [profile, setProfile] = useState({
        city: '', bio: '', zones: '', public_phone: '',
        years_experience: '', specialties: [], coverage_radius_km: 20,
        latitude: null, longitude: null
    });
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [geocoding, setGeocoding] = useState(false);
    const photoRef = useRef(null);

    const SPECIALTIES = [
        'Appartement', 'Maison', 'Luxe / Prestige', 'Investissement locatif',
        'Primo-accédant', 'Terrain', 'Commerce / Local', 'Neuf / VEFA', 'Viager'
    ];

    useEffect(() => {
        loadProfile();
        // loadProfile is a stable async fn that only reads from `api`; running once on mount is intentional.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadProfile = async () => {
        try {
            const res = await api.get('/users/me/profile');
            const d = res.data;
            setProfile({
                city: d.city || '',
                bio: d.bio || '',
                zones: (d.zones || []).join(', '),
                public_phone: d.public_phone || d.phone || '',
                years_experience: d.years_experience || '',
                specialties: d.specialties || [],
                coverage_radius_km: d.coverage_radius_km || 20,
                latitude: d.latitude || null,
                longitude: d.longitude || null
            });
            if (d.photo_storage_path) {
                setPhotoPreview(`/api/users/photo/${d.photo_storage_path}`);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            const zonesArray = profile.zones.split(',').map(z => z.trim()).filter(Boolean);
            await api.put('/users/me/profile', {
                city: profile.city,
                bio: profile.bio,
                zones: zonesArray,
                public_phone: profile.public_phone,
                years_experience: parseInt(profile.years_experience) || 0,
                specialties: profile.specialties,
                coverage_radius_km: profile.coverage_radius_km,
                latitude: profile.latitude,
                longitude: profile.longitude
            });
            toast.success('Profil mis à jour !');
        } catch (e) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const geocodeCity = async (city) => {
        if (!city || city.length < 2) return;
        setGeocoding(true);
        try {
            const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(city)}&limit=1&type=municipality`);
            const data = await res.json();
            if (data.features?.length > 0) {
                const coords = data.features[0].geometry.coordinates;
                const name = data.features[0].properties.city || data.features[0].properties.name;
                setProfile(prev => ({
                    ...prev,
                    city: name || prev.city,
                    latitude: coords[1],
                    longitude: coords[0]
                }));
                toast.success(`Position trouvée : ${name}`);
            } else {
                toast.error('Ville non trouvée');
            }
        } catch {
            toast.error('Erreur de géocodage');
        } finally {
            setGeocoding(false);
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image trop lourde (max 5 MB)');
            return;
        }
        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await api.post('/users/me/photo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setPhotoPreview(`/api/users/photo/${res.data.path}`);
            toast.success('Photo uploadée !');
        } catch (e) {
            toast.error('Erreur upload photo');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const toggleSpecialty = (spec) => {
        setProfile(prev => ({
            ...prev,
            specialties: prev.specialties.includes(spec)
                ? prev.specialties.filter(s => s !== spec)
                : [...prev.specialties, spec]
        }));
    };

    if (loading) return <Skeleton className="h-64 w-full bg-muted rounded-2xl" />;

    return (
        <motion.div variants={fadeIn}>
            <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                <div className="p-6 border-b border-border">
                    <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                        <User className="w-5 h-5 text-[#681793]" />
                        Mon Profil Public
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Visible sur Homly Réseau et par vos vendeurs
                    </p>
                </div>
                <div className="p-6 space-y-6">
                    {/* Photo */}
                    <div>
                        <Label className="text-muted-foreground">Photo de profil</Label>
                        <div className="mt-3 flex items-center gap-4">
                            <div className="w-20 h-20 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center bg-card overflow-hidden">
                                {photoPreview ? (
                                    <img src={photoPreview} alt="Photo" className="w-full h-full object-cover" />
                                ) : (
                                    <User className="w-8 h-8 text-muted-foreground" />
                                )}
                            </div>
                            <div>
                                <input type="file" ref={photoRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
                                <Button variant="outline" size="sm" onClick={() => photoRef.current?.click()} disabled={uploadingPhoto} className="border-border">
                                    {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                                    {uploadingPhoto ? 'Upload...' : 'Changer la photo'}
                                </Button>
                                <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP — max 5 MB</p>
                            </div>
                        </div>
                    </div>

                    {/* City + Geocode */}
                    <div>
                        <Label className="text-muted-foreground">Ma ville</Label>
                        <div className="flex gap-2 mt-1">
                            <Input value={profile.city} onChange={e => setProfile({...profile, city: e.target.value, latitude: null, longitude: null})} placeholder="Carpentras" className="bg-card border-border text-foreground flex-1" onBlur={() => geocodeCity(profile.city)} />
                            <Button variant="outline" size="sm" onClick={() => geocodeCity(profile.city)} disabled={geocoding} className="border-border whitespace-nowrap">
                                {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4 mr-1" />}
                                {geocoding ? '' : 'Localiser'}
                            </Button>
                        </div>
                        {profile.latitude && (
                            <p className="text-xs text-green-400 mt-1">Position GPS enregistrée</p>
                        )}
                    </div>

                    {/* Radius Slider */}
                    <div>
                        <Label className="text-muted-foreground">Zone d'intervention</Label>
                        <div className="mt-2">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-foreground font-medium">{profile.coverage_radius_km} km</span>
                                <span className="text-xs text-muted-foreground">autour de {profile.city || 'votre ville'}</span>
                            </div>
                            <input type="range" min="5" max="50" step="5" value={profile.coverage_radius_km} onChange={e => setProfile({...profile, coverage_radius_km: parseInt(e.target.value)})} className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-[#681793]" />
                            <div className="flex justify-between text-xs text-muted-foreground mt-1">
                                <span>5 km</span>
                                <span>25 km</span>
                                <span>50 km</span>
                            </div>
                        </div>
                    </div>

                    {/* Phone */}
                    <div>
                        <Label className="text-muted-foreground">Téléphone public</Label>
                        <Input value={profile.public_phone} onChange={e => setProfile({...profile, public_phone: e.target.value})} placeholder="06 00 00 00 00" className="bg-card border-border text-foreground" />
                    </div>

                    {/* Zones */}
                    <div>
                        <Label className="text-muted-foreground">Zones couvertes</Label>
                        <Input value={profile.zones} onChange={e => setProfile({...profile, zones: e.target.value})} placeholder="Carpentras, Avignon, Orange, Vaucluse..." className="bg-card border-border text-foreground" />
                        <p className="text-xs text-muted-foreground mt-1">Séparez les villes/zones par des virgules</p>
                    </div>

                    {/* Bio */}
                    <div>
                        <Label className="text-muted-foreground">Présentation</Label>
                        <textarea value={profile.bio} onChange={e => setProfile({...profile, bio: e.target.value})} placeholder="Agent immobilier spécialisé dans..." rows={3} className="w-full px-3 py-2 bg-card border border-border rounded-md text-foreground placeholder:text-muted-foreground text-sm resize-none" />
                    </div>

                    {/* Experience */}
                    <div>
                        <Label className="text-muted-foreground">Années d'expérience</Label>
                        <Input type="number" value={profile.years_experience} onChange={e => setProfile({...profile, years_experience: e.target.value})} placeholder="5" className="bg-card border-border text-foreground w-32" />
                    </div>

                    {/* Specialties */}
                    <div>
                        <Label className="text-muted-foreground">Spécialités</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {SPECIALTIES.map(spec => (
                                <button key={spec} onClick={() => toggleSpecialty(spec)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${profile.specialties.includes(spec) ? 'bg-[#681793] text-white' : 'bg-white/5 text-muted-foreground hover:bg-white/10'}`}>
                                    {spec}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Save */}
                    <div className="flex justify-end pt-4">
                        <Button onClick={handleSaveProfile} disabled={saving} className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white">
                            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enregistrement...</> : <><Save className="w-4 h-4 mr-2" />Enregistrer le profil</>}
                        </Button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

export default SettingsPage;
